export interface ChatReadPositionMessage {
  id: string;
  timestamp: number;
}

export type ChatReadPosition =
  | { mode: 'latest' }
  | { mode: 'anchor'; messageId: string };

export type ChatReadPositionTarget =
  | { mode: 'latest'; index: number }
  | { mode: 'anchor'; index: number; messageId: string };

export interface ChatScrollMetrics {
  contentHeight: number;
  viewportHeight: number;
  scrollOffset: number;
}

export interface ChatMessageLayout {
  y: number;
  height: number;
}

export interface ChatReadableMessageFromLayoutInput {
  messages: readonly ChatReadPositionMessage[];
  layouts: ReadonlyMap<string, ChatMessageLayout>;
  metrics: ChatScrollMetrics;
  bottomPadding?: number;
}

export interface ChatRestoreOffsetEstimateInput {
  index: number;
  averageItemLength: number;
}

export interface ChatReadPositionForSaveInput {
  restoredPosition: ChatReadPosition | null | undefined;
  userHasScrolled: boolean;
  isNearBottom: boolean;
  anchorMessageId: string | null | undefined;
}

export interface ChatRestoreRevealDelayInput {
  mode: ChatReadPositionTarget['mode'];
}

const CHAT_READ_POSITION_STORAGE_PREFIX = 'chat.read-position.v6';
const DEFAULT_NEAR_BOTTOM_THRESHOLD = 48;
const DEFAULT_READ_LINE_BOTTOM_PADDING = 8;
const ANCHORED_RESTORE_REVEAL_DELAY_MS = 650;

function normalizeStorageKeyPart(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase();
}

export function buildChatReadPositionStorageKey(_scope: {
  userKey: string | null | undefined;
  poolCode: string | null | undefined;
}): string {
  const userKey = normalizeStorageKeyPart(_scope.userKey);
  const poolCode = normalizeStorageKeyPart(_scope.poolCode);
  return `${CHAT_READ_POSITION_STORAGE_PREFIX}:${userKey}:${poolCode}`;
}

export function buildChatLatestPosition(): ChatReadPosition {
  return { mode: 'latest' };
}

export function buildChatAnchorPosition(messageId: string): ChatReadPosition {
  return { mode: 'anchor', messageId: messageId.trim() };
}

export function serializeChatReadPosition(position: ChatReadPosition): string {
  return JSON.stringify(position);
}

export function parseChatReadPosition(value: string | null | undefined): ChatReadPosition | null {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as unknown;
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    const candidate = parsed as Partial<ChatReadPosition> & { messageId?: unknown };
    if (candidate.mode === 'latest' && !('messageId' in candidate)) {
      return buildChatLatestPosition();
    }

    if (candidate.mode === 'anchor' && typeof candidate.messageId === 'string') {
      const messageId = candidate.messageId.trim();
      return messageId ? buildChatAnchorPosition(messageId) : null;
    }
  } catch {
    return null;
  }

  return null;
}

export function getTopVisibleChatMessageId(
  messages: readonly ChatReadPositionMessage[],
  visibleMessageIds: readonly string[]
): string | null {
  if (messages.length === 0) {
    return null;
  }

  const visibleIds = new Set(visibleMessageIds);
  const visibleMessages = messages.filter((message) => visibleIds.has(message.id));
  if (visibleMessages.length === 0) {
    return null;
  }

  return visibleMessages[0].id;
}

export function getLastVisibleChatMessageId(
  messages: readonly ChatReadPositionMessage[],
  visibleMessageIds: readonly string[]
): string | null {
  if (messages.length === 0) {
    return null;
  }

  const visibleIds = new Set(visibleMessageIds);
  const visibleMessages = messages.filter((message) => visibleIds.has(message.id));
  if (visibleMessages.length === 0) {
    return null;
  }

  return visibleMessages[visibleMessages.length - 1].id;
}

export function getLastReadableChatMessageIdFromLayouts({
  messages,
  layouts,
  metrics,
  bottomPadding = DEFAULT_READ_LINE_BOTTOM_PADDING,
}: ChatReadableMessageFromLayoutInput): string | null {
  if (
    messages.length === 0 ||
    layouts.size === 0 ||
    metrics.contentHeight <= 0 ||
    metrics.viewportHeight <= 0
  ) {
    return null;
  }

  const viewportTop = Math.max(0, metrics.scrollOffset);
  const readLine = viewportTop + metrics.viewportHeight - Math.max(0, bottomPadding);
  let lastReadableMessageId: string | null = null;

  for (const message of messages) {
    const layout = layouts.get(message.id);
    if (!layout) continue;
    if (!Number.isFinite(layout.y) || !Number.isFinite(layout.height) || layout.height <= 0) continue;

    const rowTop = layout.y;
    const rowBottom = layout.y + layout.height;
    const intersectsViewport = rowBottom >= viewportTop && rowTop <= readLine;
    if (intersectsViewport) {
      lastReadableMessageId = message.id;
    }
  }

  return lastReadableMessageId;
}

export function isChatScrolledNearBottom(
  metrics: ChatScrollMetrics,
  threshold = DEFAULT_NEAR_BOTTOM_THRESHOLD
): boolean {
  if (metrics.contentHeight <= 0 || metrics.viewportHeight <= 0) {
    return false;
  }

  const remainingDistance =
    metrics.contentHeight - (metrics.scrollOffset + metrics.viewportHeight);
  return remainingDistance <= threshold;
}

export function resolveChatReadPositionTarget(
  messages: readonly ChatReadPositionMessage[],
  position: ChatReadPosition | null | undefined
): ChatReadPositionTarget | null {
  if (messages.length === 0) {
    return null;
  }

  const latestTarget = { mode: 'latest' as const, index: messages.length - 1 };
  if (!position || position.mode === 'latest') {
    return latestTarget;
  }

  const savedIndex = messages.findIndex((message) => message.id === position.messageId);
  if (savedIndex < 0) {
    return latestTarget;
  }

  return { mode: 'anchor', index: savedIndex, messageId: position.messageId };
}

export function getChatRestoreOffsetEstimate({
  index,
  averageItemLength,
}: ChatRestoreOffsetEstimateInput): number {
  if (!Number.isFinite(index) || !Number.isFinite(averageItemLength)) {
    return 0;
  }

  return Math.max(0, index) * Math.max(0, averageItemLength);
}

export function getChatRestoreRevealDelay({ mode }: ChatRestoreRevealDelayInput): number {
  return mode === 'latest' ? 0 : ANCHORED_RESTORE_REVEAL_DELAY_MS;
}

export function getChatReadPositionForSave({
  restoredPosition,
  userHasScrolled,
  isNearBottom,
  anchorMessageId,
}: ChatReadPositionForSaveInput): ChatReadPosition | null {
  if (!userHasScrolled) {
    return restoredPosition ?? null;
  }

  if (isNearBottom) {
    return buildChatLatestPosition();
  }

  return anchorMessageId ? buildChatAnchorPosition(anchorMessageId) : null;
}
